import { Injectable } from '@angular/core';
import { Program, UserDetails } from '../interfaces/user.interface';
import { catchError, Observable, Subject, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { formatUrl } from '../shared/utils/format-url-util';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { HttpRequestService } from '../shared/services/http-request.service';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private userDetails?: UserDetails;
  private selectedProgram?: Program;
  private selectedRoleId?: number;
  private selectedMenu?: Program;
  private firstSegment?: string;
  private restrictedMenus: any[] = [];
  private programList: Program[] = [];
  private lead_entry?: string;
  private selected_lead?: string;
  private selected_id?: string;
  private pplId?: any;

  programSubject = new Subject<Program[]>();
  selectedProgramSubject = new Subject<Program>();
  selectedMenuList = new Subject<Program>();

  viewUserEvent = new Subject<Program>();

  constructor(
    private http: HttpRequestService,
    private errorHandler: GlobalErrorHandlerService
  ) { }

  // Set user details (store in-memory)
  setUserDetails(userDetails: UserDetails): void {
    this.userDetails = userDetails;
  }

  // Get user details (in-memory)
  getUserDetails(): UserDetails | undefined {
    return this.userDetails;
  }

  // Set user programs (store in-memory)
  setUserPrograms(programs: Program[]): void {
    this.programList = programs;
    this.programSubject.next(programs);
  }

  // Get the list of programs (in-memory)
  getUserPrograms(): Program[] {
    return this.programList;
  }

  setFirstSegment(segment: string): void {
    this.firstSegment = segment;
  }

  getFirstSegment(): string {
    return this.firstSegment ?? '';
  }

  setRestrictedMenus(restrictedMenus: any[]): void {
    this.restrictedMenus = restrictedMenus;
  }

  getRestrictedMenus(): any[] {
    return this.restrictedMenus;
  }

  // Set the selected program (store in-memory)
  setSelectedProgram(program: Program): void {
    this.selectedProgram = program;
    this.selectedProgramSubject.next(program);
  }

  // Get the selected program (in-memory)
  getSelectedProgram(): Program | undefined {
    return this.selectedProgram;
  }

  // Set the selected role for the current program (store in-memory)
  setSelectedRoleId(roleId?: number): void {
    this.selectedRoleId = roleId;
  }

  // Get the selected role for the current program (in-memory)
  getSelectedRoleId(): number | undefined {
    return this.selectedRoleId;
  }

  // Set the selected menu (store in-memory)
  setSelectedMenu(menu: Program): void {
    this.selectedMenu = menu;
    this.selectedMenuList.next(menu);
  }

  // Get the selected menu (in-memory)
  getSelectedMenu(): Program | undefined {
    return this.selectedMenu;
  }

  removeSelectedMenu() {
    this.selectedMenu = null as any;
  }

  // Set lead entry (store in-memory)
  setLeadEntry(lead_entry: string): void {
    this.lead_entry = lead_entry;
  }

  // Get lead entry (in-memory)
  getLeadEntry(): string | undefined {
    return this.lead_entry;
  }

  setParticipantPplId(id: any){
    this.pplId = id;
  }

  getParticipantPplId(){
    return this.pplId;
  }

  setProviderPplId(id: any){
    this.pplId = id;
  }

  getProviderPplId(){
    return this.pplId;
  }

  // Set selected lead (store in-memory)
  setSelectedLead(selected_lead: string): void {
    this.selected_lead = selected_lead;
  }

  // Get selected lead (in-memory)
  getSelectedLead(): string | undefined {
    return this.selected_lead;
  }

  // Set selected id (store in-memory)
  setSelectedId(selected_id: any): void {
    this.selected_id = selected_id;
  }

  // Get selected id (in-memory)
  getSelectedId(): string | undefined {
    return this.selected_id;
  }

  setViewUserEvent(event: any) {
    this.viewUserEvent.next(event);
  }

  // Fetch notification messages from the server
  fetchNotificationMessages(
    program_code: string,
    userType: any
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.GET_NOTIFICATIONS, {
      program_code,
      userType,
    });
    return this.http.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getBannerMessages(payload: any, isAfterUpdate = false): Observable<any> {
    return this.http.post(ENDPOINTS.GET_BANNER, payload, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  fetchAssignedVendor(programCode: string, providerId: number): Observable<any> {
    const url = formatUrl(ENDPOINTS.GET_ASSIGNED_VENDOR, {
      programCode,
      providerId,
    });
    return this.http.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  showMfaPopup(): boolean {
    const roles = ['CDPAP Facilitator Tier 2', 'FI Admin', 'MCO Admin', 'MCO Super Admin', 'LDSS Role', 'State Admin'];
    return environment.isExternal && this.isInRole(roles);
  }

  isInRole(roles: string[]): boolean {
    // check in if any role is present in selected program roles
    return roles.some((role) =>
      this.selectedProgram?.roles.some((r: any) => r.role_name === role)
    );
  }

  // Check if the given role name(s) match the currently active/selected role for the selected program
  isSelectedRole(roleNames: string | string[]): boolean {
    const names = Array.isArray(roleNames) ? roleNames : [roleNames];
    const activeRole = this.selectedProgram?.roles?.find(
      (r: any) => r.role_sid === this.selectedRoleId
    );
    return !!activeRole && names.includes(activeRole.role_name);
  }
}
